import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAnnouncements } from '../contexts/AnnouncementContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Trophy,
  LayoutDashboard,
  Activity,
  LogOut,
  User,
  Wifi,
  WifiOff,
  Plus,
  ShieldCheck,
  ChevronRight,
  Moon,
  Sun,
  Bell,
} from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { cn } from '../lib/utils';

const getInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'U';

export const Layout = ({ children, title }) => {
  const { user, logout, isOrganizer } = useAuth();
  const { unreadAnnouncements, unreadCount, markAsRead, markAllAsRead } = useAnnouncements();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isOnline, setIsOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openTournamentWorkspace = () => {
    navigate('/tournaments');
  };

  const handleAnnouncementClick = (announcement) => {
    markAsRead(announcement.id);
    navigate(`/tournaments/${announcement.tournament_id}/announcements`);
  };

  const isPathActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const isLiveNavActive =
    location.pathname === '/dashboard' && new URLSearchParams(location.search).get('tab') === 'live';

  const desktopNavItems = [
    {
      path: '/dashboard',
      label: 'Control Center',
      description: isOrganizer() ? 'Live overview and quick actions' : 'Personalized tournament hub',
      icon: LayoutDashboard,
      active: location.pathname === '/dashboard' || location.pathname === '/',
    },
    {
      path: '/tournaments',
      label: 'Tournaments',
      description: 'Fixtures, teams, standings, and announcements',
      icon: Trophy,
      active: isPathActive('/tournaments'),
    },
  ];

  const mobileNavItems = [
    {
      to: '/dashboard',
      label: 'Home',
      icon: LayoutDashboard,
      active: location.pathname === '/dashboard' && !isLiveNavActive,
    },
    {
      to: '/tournaments',
      label: 'Tournaments',
      icon: Trophy,
      active: isPathActive('/tournaments'),
    },
    {
      to: '/dashboard?tab=live',
      label: 'Live',
      icon: Activity,
      active: isLiveNavActive,
    },
  ];

  const roleLabel = isOrganizer() ? 'Organizer' : 'Viewer';
  const roleTone = isOrganizer()
    ? 'bg-primary/15 text-primary border-primary/20'
    : 'bg-emerald-500/15 text-emerald-500 dark:text-emerald-400 border-emerald-500/20';

  const themeToggleLabel = isDark ? 'Switch to light mode' : 'Switch to dark mode';

  return (
    <div
      className={cn(
        'min-h-screen text-foreground transition-colors duration-200',
        isDark
          ? 'bg-[radial-gradient(circle_at_top_left,_rgba(170,255,0,0.10),_transparent_24%),linear-gradient(180deg,_rgba(9,11,16,1)_0%,_rgba(14,18,25,1)_50%,_rgba(10,12,18,1)_100%)]'
          : 'bg-[radial-gradient(circle_at_top_left,_rgba(34,197,94,0.14),_transparent_24%),linear-gradient(180deg,_rgba(250,249,245,1)_0%,_rgba(244,247,242,1)_50%,_rgba(238,244,236,1)_100%)]'
      )}
    >
      <div
        className={cn(
          'pointer-events-none fixed inset-0 [background-size:32px_32px]',
          isDark
            ? 'opacity-40 [background-image:linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)]'
            : 'opacity-70 [background-image:linear-gradient(rgba(15,23,42,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.04)_1px,transparent_1px)]'
        )}
      />

      <aside
        className={cn(
          'hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:flex lg:w-[284px] lg:flex-col lg:border-r lg:backdrop-blur-xl',
          isDark ? 'lg:border-white/10 lg:bg-black/25' : 'lg:border-slate-900/10 lg:bg-white/70'
        )}
      >
        <div className={cn('px-6 py-6', isDark ? 'border-b border-white/10' : 'border-b border-slate-900/10')}>
          <Link to="/dashboard" className="flex items-center gap-3">
            <BrandLogo compact markClassName="shrink-0" textClassName={isDark ? 'text-white' : 'text-slate-950'} />
          </Link>
          <div
            className={cn(
              'mt-6 rounded-2xl border p-4',
              isDark ? 'border-white/10 bg-white/[0.04]' : 'border-slate-900/10 bg-white/85 shadow-sm'
            )}
          >
            <div className="flex items-center justify-between">
              <span className={cn('text-xs uppercase tracking-[0.24em]', isDark ? 'text-white/45' : 'text-slate-500')}>
                Session
              </span>
              <span className={cn('rounded-full border px-2.5 py-1 text-[11px] font-semibold', roleTone)}>
                {roleLabel}
              </span>
            </div>
            <p className={cn('mt-3 text-lg font-semibold', isDark ? 'text-white' : 'text-slate-950')}>{user?.name}</p>
            <p className={cn('text-sm', isDark ? 'text-white/50' : 'text-slate-500')}>{user?.email}</p>
            <div className={cn('mt-4 flex items-center gap-2 text-xs', isDark ? 'text-white/55' : 'text-slate-600')}>
              {isOnline ? <Wifi className="h-3.5 w-3.5 text-emerald-400" /> : <WifiOff className="h-3.5 w-3.5 text-red-400" />}
              <span>{isOnline ? 'Connected to live data' : 'Offline mode detected'}</span>
            </div>
          </div>
        </div>

        <div className="flex-1 px-4 py-5">
          <div className={cn('mb-3 px-3 text-[11px] font-semibold uppercase tracking-[0.28em]', isDark ? 'text-white/35' : 'text-slate-500')}>
            Navigation
          </div>
          <nav className="space-y-2">
            {desktopNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'group flex items-center gap-3 rounded-2xl border px-3 py-3 transition-all',
                    item.active
                      ? cn(
                          'border-primary/30 bg-primary/12 shadow-[0_0_0_1px_rgba(170,255,0,0.12)]',
                          isDark ? 'text-white' : 'text-slate-950'
                        )
                      : isDark
                        ? 'border-transparent bg-white/[0.03] text-white/68 hover:border-white/10 hover:bg-white/[0.06] hover:text-white'
                        : 'border-transparent bg-slate-900/[0.03] text-slate-600 hover:border-slate-900/10 hover:bg-white/90 hover:text-slate-950'
                  )}
                >
                  <div
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-xl border',
                      item.active
                        ? 'border-primary/30 bg-primary/15 text-primary'
                        : isDark
                          ? 'border-white/10 bg-black/20'
                          : 'border-slate-900/10 bg-slate-900/[0.04] text-slate-700'
                    )}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{item.label}</p>
                    <p className={cn('truncate text-xs', isDark ? 'text-white/45' : 'text-slate-500')}>
                      {item.description}
                    </p>
                  </div>
                  <ChevronRight
                    className={cn(
                      'h-4 w-4 transition-transform',
                      item.active ? 'text-primary' : isDark ? 'text-white/30 group-hover:translate-x-0.5' : 'text-slate-400 group-hover:translate-x-0.5'
                    )}
                  />
                </Link>
              );
            })}
          </nav>

          {isOrganizer() && (
            <div className={cn('mt-6 rounded-2xl border border-primary/15 p-4', isDark ? 'bg-primary/[0.08]' : 'bg-primary/[0.06]')}>
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-primary/15 p-2 text-primary">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={cn('text-sm font-semibold', isDark ? 'text-white' : 'text-slate-950')}>Organizer Quick Action</p>
                  <p className={cn('mt-1 text-xs leading-5', isDark ? 'text-white/55' : 'text-slate-600')}>
                    Jump into tournament setup, registration management, and fixture operations.
                  </p>
                </div>
              </div>
              <Button className="mt-4 w-full justify-between rounded-xl" onClick={openTournamentWorkspace}>
                Open Tournament Workspace
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        <div className={cn('px-4 py-4', isDark ? 'border-t border-white/10' : 'border-t border-slate-900/10')}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  'flex w-full items-center gap-3 rounded-2xl border px-3 py-3 text-left transition-colors',
                  isDark ? 'border-white/10 bg-white/[0.04] hover:bg-white/[0.07]' : 'border-slate-900/10 bg-white/85 hover:bg-white'
                )}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/20 text-sm font-semibold text-primary">
                  {getInitials(user?.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={cn('truncate text-sm font-semibold', isDark ? 'text-white' : 'text-slate-950')}>{user?.name}</p>
                  <p className={cn('truncate text-xs', isDark ? 'text-white/45' : 'text-slate-500')}>{roleLabel}</p>
                </div>
                <User className={cn('h-4 w-4', isDark ? 'text-white/40' : 'text-slate-400')} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuItem className="text-muted-foreground text-xs">
                {user?.email}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-red-500" data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className="relative lg:pl-[284px]">
        <header
          className={cn(
            'sticky top-0 z-30 border-b backdrop-blur-xl',
            isDark ? 'border-white/10 bg-[#0c1017]/80' : 'border-slate-900/10 bg-[rgba(250,249,245,0.86)]'
          )}
        >
          <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between px-4 sm:px-6 lg:h-20 lg:px-8">
            <div className="flex min-w-0 items-center gap-3">
              <div className="lg:hidden">
                <Link to="/dashboard" className="flex items-center gap-2">
                  <BrandLogo compact markClassName="shrink-0" textClassName={cn('hidden sm:block', isDark ? 'text-white' : 'text-slate-950')} />
                </Link>
              </div>
              <div className="min-w-0">
                {title && <h1 className={cn('truncate text-xl font-semibold tracking-tight sm:text-2xl', isDark ? 'text-white' : 'text-slate-950')}>{title}</h1>}
                <p className={cn('hidden text-xs uppercase tracking-[0.24em] sm:block', isDark ? 'text-white/38' : 'text-slate-500')}>
                  {isOrganizer() ? 'Tournament operations center' : 'Live tournament hub'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <div
                className={cn(
                  'hidden rounded-full border px-3 py-1.5 text-xs sm:flex sm:items-center sm:gap-2',
                  isDark ? 'border-white/10 bg-white/[0.05] text-white/60' : 'border-slate-900/10 bg-white/80 text-slate-600'
                )}
              >
                {isOnline ? <Wifi className="h-3.5 w-3.5 text-emerald-400" /> : <WifiOff className="h-3.5 w-3.5 text-red-400" />}
                <span>{isOnline ? 'Live sync on' : 'Offline'}</span>
              </div>

              <button
                type="button"
                onClick={toggleTheme}
                aria-label={themeToggleLabel}
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-2xl border transition-colors',
                  isDark
                    ? 'border-white/10 bg-white/[0.05] text-white hover:bg-white/[0.08]'
                    : 'border-slate-900/10 bg-white/80 text-slate-700 hover:bg-white'
                )}
              >
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Unread announcements"
                    className={cn(
                      'relative flex h-10 w-10 items-center justify-center rounded-2xl border transition-colors',
                      isDark
                        ? 'border-white/10 bg-white/[0.05] text-white hover:bg-white/[0.08]'
                        : 'border-slate-900/10 bg-white/80 text-slate-700 hover:bg-white'
                    )}
                  >
                    <Bell className="h-4 w-4" />
                    {unreadCount > 0 && (
                      <span className="absolute -right-1 -top-1 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[320px] rounded-2xl p-2">
                  <div className="flex items-center justify-between px-2 pb-1 pt-1">
                    <DropdownMenuLabel className="p-0 text-sm">Unread Announcements</DropdownMenuLabel>
                    {unreadCount > 0 && (
                      <button type="button" onClick={markAllAsRead} className="text-xs font-medium text-primary">
                        Mark all read
                      </button>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  {unreadAnnouncements.length === 0 ? (
                    <div className="px-2 py-4 text-sm text-muted-foreground">
                      No unread announcements right now.
                    </div>
                  ) : (
                    unreadAnnouncements.slice(0, 5).map((announcement) => (
                      <DropdownMenuItem
                        key={announcement.id}
                        onClick={() => handleAnnouncementClick(announcement)}
                        className="flex flex-col items-start gap-1 rounded-xl px-3 py-3"
                      >
                        <div className="flex w-full items-start justify-between gap-3">
                          <span className="line-clamp-1 font-medium">{announcement.title}</span>
                          <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-red-500" />
                        </div>
                        <p className="line-clamp-2 text-xs text-muted-foreground">{announcement.message}</p>
                        <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                          {announcement.tournament_name}
                        </p>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {isOrganizer() && (
                <Button
                  onClick={openTournamentWorkspace}
                  className="hidden rounded-full bg-primary text-black hover:bg-primary/90 sm:inline-flex"
                >
                  <Plus className="h-4 w-4" />
                  Create Tournament
                </Button>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-2xl border transition-colors',
                      isDark
                        ? 'border-white/10 bg-white/[0.05] text-white hover:bg-white/[0.08]'
                        : 'border-slate-900/10 bg-white/80 text-slate-950 hover:bg-white'
                    )}
                    data-testid="user-menu-trigger"
                  >
                    <span className="text-sm font-semibold">{getInitials(user?.name)}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem className="text-muted-foreground text-xs">
                    {user?.email}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-500">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-[1600px] px-4 pb-28 pt-5 sm:px-6 lg:px-8 lg:pb-10 lg:pt-8">
          <div
            className={cn(
              'mb-6 rounded-3xl border px-4 py-4 shadow-[0_20px_60px_rgba(0,0,0,0.18)] sm:px-6',
              isDark ? 'border-white/10 bg-white/[0.04]' : 'border-slate-900/10 bg-white/80 shadow-[0_20px_60px_rgba(15,23,42,0.08)]'
            )}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className={cn('text-[11px] font-semibold uppercase tracking-[0.28em]', isDark ? 'text-white/35' : 'text-slate-500')}>
                  Matchday Stream
                </p>
                <p className={cn('mt-1 text-sm', isDark ? 'text-white/68' : 'text-slate-600')}>
                  {isOrganizer()
                    ? 'Operate tournaments, resolve conflicts, and keep live operations moving.'
                    : 'Track live action, standings, and tournament updates in one place.'}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className={cn('rounded-full border px-3 py-1.5 text-xs font-semibold', roleTone)}>
                  {roleLabel}
                </span>
                <span
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-xs',
                    isDark ? 'border-white/10 bg-black/20 text-white/55' : 'border-slate-900/10 bg-slate-900/[0.03] text-slate-600'
                  )}
                >
                  {isOnline ? 'Realtime available' : 'Using cached data'}
                </span>
              </div>
            </div>
          </div>

          {children}
        </main>

        <nav
          className={cn(
            'fixed inset-x-0 bottom-0 z-40 border-t px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-3 backdrop-blur-xl lg:hidden',
            isDark ? 'border-white/10 bg-[#0b0f15]/95' : 'border-slate-900/10 bg-[rgba(250,249,245,0.95)]'
          )}
        >
          <div className="mx-auto flex max-w-lg items-end justify-between gap-2">
            {mobileNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  className={cn(
                    'flex min-w-0 flex-1 flex-col items-center gap-1 rounded-2xl px-2 py-2 text-[11px] font-medium transition-all',
                    item.active ? 'bg-primary/14 text-primary' : isDark ? 'text-white/52' : 'text-slate-500'
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    'flex min-w-0 flex-1 flex-col items-center gap-1 rounded-2xl px-2 py-2 text-[11px] font-medium transition-all',
                    isDark ? 'text-white/52 hover:bg-white/[0.05]' : 'text-slate-500 hover:bg-slate-900/[0.05]'
                  )}
                >
                  <User className="h-5 w-5" />
                  <span>Profile</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem className="text-muted-foreground text-xs">
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-red-500">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </nav>

        {isOrganizer() && (
          <Button
            onClick={openTournamentWorkspace}
            size="icon"
            className="fixed bottom-24 right-4 z-40 h-14 w-14 rounded-2xl bg-primary text-black shadow-[0_18px_35px_rgba(170,255,0,0.26)] hover:bg-primary/90 lg:hidden"
            aria-label="Create Tournament"
          >
            <Plus className="h-6 w-6" />
          </Button>
        )}
      </div>
    </div>
  );
};
